import { Suspense } from 'react'
import { AppSectionNav } from './app-section-nav'
import { BottomNavSkeleton } from './app-layout-skeleton'
import { useTopLevelSections } from './use-top-level-sections'

// The mobile section nav: below `md` the links move out of the topbar (where 5-6 of them collapsed into a
// row of 10px truncated labels fighting the logo and the avatar for width) and become a thumb-reachable bar
// pinned to the bottom of the shell.
//
// It's a FLEX SIBLING of <main> inside the h-dvh shell, not `fixed` — the shell never scrolls (<main> owns
// the scroll), so an in-flow bar is permanently on screen exactly like a fixed one, while <main> keeps
// shrinking around it. No overlay, so no bottom padding to keep in sync and nothing to hide content behind.
// Horizontal sizing mirrors <main> (mx-2 + max-w-9xl) so the bar lines up with the content card above it.
export function BottomNavShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      data-testid="app-bottom-nav"
      className="max-w-9xl bg-background mx-2 mb-2 w-full shrink-0 rounded-xl border md:hidden print:hidden"
      // The iOS home indicator sits inside dvh, so pad the bar (not <main>) out from under it.
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {children}
    </div>
  )
}

function AppBottomNavContent() {
  // Same roster (and gate) as the section pager: a viewer who can only reach one section has nothing to
  // navigate between, so the bar would be inert chrome eating vertical space on the smallest screens.
  const sections = useTopLevelSections()
  if (sections.length <= 1) return null

  return (
    <BottomNavShell>
      <AppSectionNav variant="bottom" />
    </BottomNavShell>
  )
}

export function AppBottomNav() {
  return (
    <Suspense
      fallback={
        <BottomNavShell>
          <BottomNavSkeleton />
        </BottomNavShell>
      }
    >
      <AppBottomNavContent />
    </Suspense>
  )
}
